import React, { useState } from 'react';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AdminInfoForm } from './forms/AdminInfoForm';
import { OrganizationInfoForm } from './forms/OrganizationInfoForm';
import { RegistrationComplete } from './forms/RegistrationComplete';
import { EmailConfirmationPending } from './forms/EmailConfirmationPending';
import { useCreateOrganization } from '../hooks/useOrganization';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { UserOrganizationService } from '../services/userOrganizationService';
import { OrganizationService } from '../services/organizationService';

interface AdminInfo {
  email: string;
  password: string;
  displayName: string;
}

interface OrganizationInfo {
  name: string;
  address?: string;
  postalCode?: string;
  totalUnits?: number;
  fiscalYearStartMonth?: number;
  managementCompany?: string;
  establishedDate?: string;
  contactEmail?: string;
  contactPhone?: string;
}

const steps = ['管理者情報', 'マンション情報', '登録完了'];

interface OrganizationRegistrationProps {
  onBack?: () => void;
}

export const OrganizationRegistration: React.FC<OrganizationRegistrationProps> = ({ onBack }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [adminInfo, setAdminInfo] = useState<AdminInfo | null>(null);
  const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [registeredOrganization, setRegisteredOrganization] = useState<any>(null);
  const [isEmailConfirmationPending, setIsEmailConfirmationPending] = useState(false);
  
  const createOrganization = useCreateOrganization();
  const { signIn } = useAuth();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAdminInfoSubmit = (data: AdminInfo) => {
    setAdminInfo(data);
    handleNext();
  };

  const handleOrganizationInfoSubmit = async (data: OrganizationInfo) => {
    setOrganizationInfo(data);
    setIsLoading(true);
    setError(null);

    try {
      if (!adminInfo) {
        throw new Error('管理者情報が見つかりません');
      }

      // 1. 管理者アカウントを作成
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: adminInfo.email,
        password: adminInfo.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`,
        }
      });

      if (signUpError) {
        throw new Error(`アカウント作成エラー: ${signUpError.message}`);
      }

      if (!signUpData?.user) {
        throw new Error('ユーザー作成に失敗しました');
      }

      // 2. 組織を作成（新しい関数を使用）
      const { data: result, error: orgError } = await supabase
        .rpc('create_organization_and_admin_membership', {
          p_org_data: {
            name: data.name,
            address: data.address || null,
            postalCode: data.postalCode || null,
            totalUnits: data.totalUnits || null,
            fiscalYearStartMonth: data.fiscalYearStartMonth || 4,
            managementCompany: data.managementCompany || null,
            establishedDate: data.establishedDate || null,
            contactEmail: data.contactEmail || adminInfo.email,
            contactPhone: data.contactPhone || null,
          },
          p_user_id: signUpData.user.id,
          p_display_name: adminInfo.displayName,
        });

      if (orgError || !result?.[0]?.success) {
        throw new Error(result?.[0]?.message || `組織作成エラー: ${orgError?.message}`);
      }

      const organizationId = result[0].organization_id;

      // 組織情報を取得
      const organization = await OrganizationService.getOrganizationById(organizationId);
      setRegisteredOrganization(organization);

      // メール確認が必要かどうかをチェック
      if (!signUpData.session) {
        setIsEmailConfirmationPending(true);
        return; // メール確認待ち画面を表示
      }

      // セッションがある場合は即座にログイン完了
      handleNext();
      
      // onBackを呼んで登録画面フラグをリセット（登録完了時）
      if (onBack) {
        setTimeout(() => {
          onBack();
        }, 100);
      }
    } catch (error: any) {
      setError(error.message || '登録処理中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  const getStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (
          <AdminInfoForm
            onSubmit={handleAdminInfoSubmit}
            onBack={onBack || (() => window.history.back())}
          />
        );
      case 1:
        return (
          <OrganizationInfoForm
            onSubmit={handleOrganizationInfoSubmit}
            onBack={handleBack}
            isLoading={isLoading}
          />
        );
      case 2:
        return (
          <RegistrationComplete
            organizationName={registeredOrganization?.name || ''}
            adminEmail={adminInfo?.email || ''}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  // メール確認待ち画面を表示
  if (isEmailConfirmationPending && adminInfo) {
    return (
      <EmailConfirmationPending
        email={adminInfo.email}
        onBackToLogin={onBack}
      />
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          マンション管理組合 新規登録
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ minHeight: '400px' }}>
          {getStepContent(activeStep)}
        </Box>

        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                登録処理中...
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};